import * as React from 'react';
import styled from 'styled-components';
import {COLORS, ComponentTextStyle, focusStyles} from '../styles';

import {
  ComponentLStyling,
  ComponentMStyling,
  ComponentSStyling,
  ComponentXSStyling,
  ComponentXXSStyling
} from '../styles';
import {DropdownButtonProps} from '../Dropdown/DropdownButtonTypes';
import {TooltipWrapper} from '../Tooltips';
import {DropdownButton} from '../Dropdown';
import {SystemIcons} from '../icons';
import {Size} from '../types';
import {Z_INDEXES} from '../styles';
import {defaultOnMouseDownHandler} from '../common';
import {IconButton} from '../Button';

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid ${props => COLORS.generateToken({ componentType:'border', defaultVariant:'subtle' }, props.theme)};
  box-sizing: border-box;

  color: ${props => COLORS.generateToken({componentType:'text',defaultVariant:'subtle'}, props.theme)};
  background-color: ${props => COLORS.generateToken({componentType:'bg-surface', defaultVariant:'default'}, props.theme)};


  cursor: default;

  &.interactive {
    cursor: pointer;
  }

  &.interactive.disabled {
    cursor: not-allowed;
  }

  &.interactive:hover:not(.in-action):not(.disabled) {
    background-color: ${props => COLORS.generateToken({ componentType:'bg-surface', state:'hover' }, props.theme)};
    color: ${props => COLORS.generateToken({ componentType: 'text', state:'hover' }, props.theme)};
    z-index: ${Z_INDEXES.hover};
  }

  &.interactive:active:not(.in-action):not(.disabled) {
    background-color: ${props => COLORS.generateToken({ componentType:'bg-surface', state:'active' }, props.theme)};
    color: ${props => COLORS.generateToken({ componentType:'text', state:'active' }, props.theme)};
    z-index: ${Z_INDEXES.active};
  }

  &.interactive:focus {
    ${focusStyles}
  }

  &.disabled {
    .list-item-icon, .list-item-content, .list-item-note, .list-item-actions {
      color: ${props => COLORS.generateToken({ componentType: 'text', state: 'disabled' }, props.theme)};
    }
  }


  &.small {
    min-height: 48px;
    padding: 0 8px;

    .list-item-icon {
      margin-right: 8px;
    }

    .list-item-main-text {
      ${ComponentSStyling(ComponentTextStyle.Regular, null)}
    }

    .list-item-actions {
      margin-right: -8px;
    }
  }

  &.medium {
    min-height: 56px;
    padding: 0 12px;

    .list-item-icon {
      margin-right: 12px;
    }

    .list-item-main-text {
      ${ComponentMStyling(ComponentTextStyle.Regular, null)}
    }

    .list-item-actions {
      margin-right: -12px;
    }
  }

  &.large {
    min-height: 64px;
    padding: 0 16px;

    .list-item-icon {
      margin-right: 16px;
    }

    .list-item-main-text {
      ${ComponentLStyling(ComponentTextStyle.Regular, null)}
    }

    .list-item-actions {
      margin-right: -16px;
    }
  }

  &.overlay {
    padding: 0 4px;

    .list-item-actions {
      margin-right: -4px;
    }
  }

  .list-item-icon {
    width: 24px;
    height: 24px;

    svg {
      width: 24px;
      height: 24px;
    }
  }

  .list-item-content {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }

  .list-item-main-text {
  }

  .list-item-secondary-text {
    ${ComponentXXSStyling(ComponentTextStyle.Regular, null)}
  }

  .list-item-note {
    color: ${props => COLORS.generateToken({componentType:'text',defaultVariant:'subtle'}, props.theme)};
    word-break: initial;
    ${ComponentXSStyling(ComponentTextStyle.Regular, null)}
  }

  .list-item-note.with-tooltip {
    cursor: pointer;

    > div {
      text-decoration-line: underline;
      text-decoration-style: dashed;
      text-underline-offset: 1px;
      text-decoration-thickness: 1px;
    }
  }

  .list-item-actions {
    display: flex;
    align-items: center;

    button div {
      background-color: transparent;
    }
  }
`;

const RightIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  min-width: 48px;
`;


export interface ListRowProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'tabIndex' | 'onMouseDown' | 'onKeyDown'> {
  /** Size of the ListRow. Defaults to 'medium'. */
  size?: Size.Small | Size.Medium | Size.Large;
  /** Variant of the ListRow. Defaults to 'normal'. */
  variant?: 'normal' | 'overlay';
  /** Optional. Icon to be shown on the left side of the ListRow. */
  icon?: React.ReactNode;
  /** Required. Main text of the ListRow */
  mainText: string;
  /** Optional. Secondary row shown under MainText  */
  secondaryText?: string;
  /** Optional. Details of the DropdownButton shown on the right side of the ListRow.  */
  dropdown?: Pick<DropdownButtonProps, 'items' | 'onClick' | 'disabled' | 'width'>;
  /** Optional. Note to be shown on the right side of the component.  */
  note?: string | React.ReactNode;
  /** Optional. Tooltip of the Note shown on the right side of the component.  */
  noteTooltip?: string;
  /** Optional. If disabled then styles are greyed out and user can not interact with dropdown or click on the ListRow. */
  disabled?: boolean;
  /** Optional. Action to be executed when user clicks the row. */
  action?: () => void;
  /** Optional. Icon of the IconButton to show on the right side of the component */
  rightSideIcon?: React.ReactNode;
  /** Optional. Action to be executed when user clicks on the IconButton on the right */
  rightSideAction?: () => void;
  /** Optional. If disabled user will not be able to interact with IconButton shown on the right side */
  rightSideIconDisabled?: boolean;
};

const ListRow: React.FunctionComponent<ListRowProps> = ({
                                                   size = Size.Medium,
                                                   variant = 'normal',
                                                   icon,
                                                   mainText,
                                                   secondaryText,
                                                   dropdown,
                                                   note,
                                                   noteTooltip,
                                                   disabled = false,
                                                   action,
                                                   rightSideIcon,
                                                   rightSideAction,
                                                   rightSideIconDisabled,
                                                   className,
                                                   ...rest
                                                 }: ListRowProps) => {

  const [inAction, setInAction] = React.useState<Boolean>(false);

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      action && action()
    }
  }

  const rowClassName = (inAction ? 'in-action ' : '')
    .concat(disabled ? 'disabled ' : '')
    .concat(!!action ? 'interactive ' : '')
    .concat('list-item-row ')
    .concat(`${variant} `)
    .concat(`${size}`)
    .concat(className ? ` ${className}` : '');

  return (
    <Row tabIndex={!action || disabled ? -1 : 0}
         className={rowClassName}
         onMouseDown={defaultOnMouseDownHandler}
         onKeyDown={(e) => !disabled && handleKeyDown(e)}
         onClick={e => {
           !disabled && action && action();
         }}
         data-hj-suppress
         {...rest}>
      {
        !!icon &&
        <div className={'list-item-icon'}>{icon}</div>
      }
      <div className={'list-item-content'}>
        <div className={'list-item-main-text'}>{mainText}</div>
        {!!secondaryText && <div className={'list-item-secondary-text'}>{secondaryText}</div>}
      </div>
      {
        !!note &&
        <>
          {
            !!noteTooltip &&
            <div className={'list-item-note with-tooltip'}>
              <TooltipWrapper label={noteTooltip}
                              size={size === Size.Large ? Size.Medium : size === Size.Small ? Size.XSmall : Size.Small}
                              position={'top'}
                              tabIndex={disabled ? -1 : 0}
                              withArrow={true}>
                {note}
              </TooltipWrapper>
            </div>
          }
          {
            !noteTooltip &&
            <div className={'list-item-note'}>
              {note}
            </div>
          }
        </>
      }
      {
        !!dropdown &&
        <div className={'list-item-actions'}
             onMouseEnter={() => setInAction(!dropdown?.disabled)}
             onMouseLeave={() => setInAction(false)}>
          <DropdownButton {...dropdown}
                          type={'icon'}
                          disabled={disabled || dropdown.disabled}
                          icon={variant === 'overlay'
                            ? <SystemIcons.ArrowDropDown/>
                            : <SystemIcons.MoreVertical/>}
          />
        </div>
      }
      {
        !!rightSideIcon &&
        <RightIcon className={'list-item-actions'}
                   onMouseEnter={() => rightSideAction && setInAction(!dropdown?.disabled)}
                   onMouseLeave={() => rightSideAction && setInAction(false)}>
          {
            rightSideAction &&
            <IconButton action={rightSideAction} variant='secondary' shape='circular'
                        disabled={disabled || rightSideIconDisabled}>
              {rightSideIcon}
            </IconButton>
          }
          {
            !rightSideAction && rightSideIcon
          }
        </RightIcon>
      }
    </Row>
  );
};

export default ListRow;

